import { FeaturedWorkoutCard } from './featured-workout-card'
import React from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { SectionTitle } from 'src/component/ui/section-title'
import { WorkoutCardSkeleton } from 'src/component/ui/workout-card/workout-card-skeleton'
import { FeaturedWorkout } from 'src/dao/core-dao'
import { useFeaturedShelfViewed } from 'src/hook/use-featured-shelf-viewed'
import { useFeaturedWorkouts } from 'src/hook/use-featured-workouts'
import { FeaturedSurface, MixpanelEvent, mixpanelService } from 'src/service/mixpanel-service'

const SKELETON_COUNT = 3

interface FeaturedWorkoutsSectionProps {
  /** What a card's play control does. Differs per surface, so the section does not decide it. */
  onSelectWorkout: (workout: FeaturedWorkout) => void
  /** Which shelf this is — the two surfaces convert very differently, so events say which. */
  surface: FeaturedSurface
  className?: string
}

/**
 * The free-workout shelf on Home, for accounts without full catalog access.
 *
 * It deliberately reads no account state — no `SessionContext`, no `useCatalogAccess`. Whether
 * it should appear at all is the caller's decision (`home-featured-workouts.tsx`).
 *
 * The sign-in page does **not** use this. It has two placements of its own —
 * `featured-workouts-hero-panel.tsx` at `lg` and up, `featured-workouts-rail.tsx` below — both
 * of which have to fit a fixed height rather than stack down a page, which is the one thing a
 * shelf of `xs={12}` rows cannot do.
 *
 * An empty set renders **nothing**: no heading, no empty state, no placeholder. A shelf
 * advertising free workouts with none in it is worse than no shelf.
 */
export const FeaturedWorkoutsSection = ({
  onSelectWorkout,
  surface,
  className,
}: FeaturedWorkoutsSectionProps): React.ReactElement => {
  const { featuredWorkouts, isLoading } = useFeaturedWorkouts()
  useFeaturedShelfViewed({ surface, workoutCount: featuredWorkouts.length })

  if (!isLoading && featuredWorkouts.length === 0) return <></>

  /*
   * A light section, matching Trending Express: ivory ground, black text, and the same rhythm
   * (40px top / 48px bottom / 48px gap). The subtitle's `text-primary-gray` is `#1F1F1F`, which
   * only reads as intended against this ground — on the dark one it was all but invisible.
   *
   * Home also puts `whiteBg` on the wrapping Row, because this `Container` is max-width
   * constrained and a ground set only here stops short of the viewport edges. Keep both, as
   * Express does.
   */
  return (
    <Container
      data-testid="featured-workouts-section"
      data-nextstep="featured-workouts"
      className={`bg-ivory text-black w-full pt-10 pb-12 flex flex-col gap-12 ${className ?? ''}`}
    >
      <Row className="flex flex-nowrap justify-between items-center gap-4">
        <div className="flex flex-col gap-2">
          <SectionTitle>Featured workouts</SectionTitle>
          {/* Typography matches the signed-out surfaces (`featured-workouts-rail`,
              `featured-workouts-hero-panel`) so the same line reads the same on both sides of
              sign-in. Only the colour differs: they sit on near-black and use `text-white`,
              this sits on ivory and keeps the grey. */}
          <p className="font-roboto-condensed text-base font-bold leading-6 tracking-[0.5px] text-primary-gray m-0">
            Get a flavor of our workouts.
          </p>
        </div>
      </Row>
      <Container fluid className="px-0">
        <Row>
          {isLoading
            ? Array.from({ length: SKELETON_COUNT }, (_, index) => (
                <Col key={`featured-skeleton-${index}`} xs={12} lg={4} className="mb-7 lg:mb-0">
                  <WorkoutCardSkeleton />
                </Col>
              ))
            : // Rendered in the order the server gave them — shortest to longest. Do not sort.
              featuredWorkouts.map((workout) => (
                <Col key={workout.id} xs={12} lg={4} className="mb-7 lg:mb-0">
                  {/* The card owns the click via `onAction`; this wrapper is layout only. Do not
                      add an onClick here too — it would fire the handler twice per click. */}
                  <div className="h-full">
                    <FeaturedWorkoutCard
                      workout={workout}
                      surface={surface}
                      onAction={() => {
                        mixpanelService.trackFeatured(MixpanelEvent.FEATURED_WORKOUT_PLAY_CLICKED, {
                          workoutId: workout.id,
                          workoutTitle: workout.title,
                          surface,
                          signedIn: surface === 'home',
                        })
                        onSelectWorkout(workout)
                      }}
                    />
                  </div>
                </Col>
              ))}
        </Row>
      </Container>
    </Container>
  )
}
